import { useLayoutEffect, useRef } from "react";

// Native intrinsic text, never allocated wrapper widths: resizing must be able
// to shrink again. Collection-only; retained Plan readers keep their geometry.
export function useMarketsListGeometry(ref, enabled, markets, density, financial, pending = false) {
  const measureRef = useRef(null);
  const populated = markets.length > 0;
  useLayoutEffect(() => {
    const shell = ref.current;
    if (!enabled || !shell || !document.createRange().getBoundingClientRect) return undefined;
    let disposed = false;
    let fontsReady = !document.fonts || document.fonts.status !== "loading";
    let signature;
    const textWidth = node => {
      if (!node) return 0;
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      const bounds = [];
      while (walker.nextNode()) {
        if (!walker.currentNode.textContent.trim() || walker.currentNode.parentElement.closest("svg")) continue;
        const range = document.createRange();
        range.selectNodeContents(walker.currentNode);
        bounds.push(range.getBoundingClientRect());
      }
      return bounds.length ? Math.max(...bounds.map(b => b.right)) - Math.min(...bounds.map(b => b.left)) : 0;
    };
    const measure = (force = false) => {
      if (disposed || pending || !fontsReady) return;
      const header = shell.querySelector('[data-testid="plan-column-header"] [role="row"]');
      const rows = [...shell.querySelectorAll('[data-testid^="matrix-row-"]')];
      if (!header || !rows.length) return;
      // Object identity and sort order are not content changes. Polling must
      // not remeasure unchanged text or tear down the resize/font observers.
      const nextSignature = JSON.stringify([header.textContent, rows.map(row => row.textContent).sort()]);
      if (!force && nextSignature === signature) return;
      signature = nextSignature;
      const setProperty = (key, value) => {
        if (shell.style.getPropertyValue(key) !== value) shell.style.setProperty(key, value);
      };
      const pills = rows.map(row => row.querySelector(".markets-capacity-pill")).filter(Boolean);
      const pillWidth = Math.ceil(Math.max(0, ...pills.map(p => textWidth(p.firstElementChild)))) + 16;
      setProperty("--markets-pill-width", `${pillWidth}px`);
      const headers = [...header.children];
      const minima = headers.map((h, i) => {
        const label = h.querySelector(".sort-header-label") || h;
        const caret = h.querySelector("svg");
        const padding = i === 0 || i === headers.length - 1 ? 28 : 8;
        return Math.ceil(textWidth(label) + Math.max(padding, caret ? 20 : 0));
      });
      for (const row of rows) [...row.children].forEach((cell, i) => {
        // Ranges preserve all inline gaps; flex dates need their explicit gaps
        // included too. Badges contribute their own intrinsic padding/width.
        let width = textWidth(cell);
        const badge = cell.querySelector('[data-testid^="strategy-pill-"]');
        if (badge) width = badge.getBoundingClientRect().width;
        if (i === 4) {
          const progress = cell.querySelector('[data-testid="progress-dots"]');
          if (progress) width = (progress.firstElementChild?.getBoundingClientRect().width ?? 0)
            + textWidth(progress.lastElementChild) + (parseFloat(getComputedStyle(progress).columnGap) || 0);
        }
        if (i === 5) width = pillWidth;
        const date = cell.querySelector('[data-testid^="market-next-date-"]');
        if (date?.children.length) {
          // Sum intrinsic leaves plus flex gaps. Clipped flex children can
          // overlap glyph ranges, understating their combined intrinsic width.
          width = [...date.children].reduce((sum, child) => sum + textWidth(child), 0)
            + (parseFloat(getComputedStyle(date).columnGap) || 0) * (date.children.length - 1);
        }
        const padding = i === 0 || i === headers.length - 1 ? 18 : 8;
        minima[i] = Math.max(minima[i], Math.ceil(width) + padding);
      });
      setProperty("--markets-columns", minima.map(w => `minmax(${w}px, 1fr)`).join(" "));
      setProperty("--markets-min-width", `${minima.reduce((a, b) => a + b, 0)}px`);
      shell.dataset.marketsReady = "true";
    };
    measureRef.current = measure;
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => measure(true));
    observer?.observe(shell);
    document.fonts?.ready.then(() => { fontsReady = true; measure(); });
    return () => { disposed = true; measureRef.current = null; observer?.disconnect(); };
  }, [ref, enabled, populated, density, financial, pending]);
  // Check rendered labels after commits without coupling layout to data-object
  // replacement. Measurement itself only runs for changed content.
  useLayoutEffect(() => { measureRef.current?.(); });
}
